import { getCachedState } from 'redux/cacheables/helpers/cacheStateChanges'
import { ADD_TYPES } from 'types/deck'

export type CachablesState = {
  textColumns: TextColumns
  imageCardLayers: ImageCardLayers

  imageSize: ImageSize
  disableMargins: boolean
  showEmptyCardStacks: boolean
  hideDefaultTypeCategories: boolean
  pinnedCategory: boolean
  draggableStacks: boolean
  hideOutOfDeckCategories: boolean
  disableDeckCursorCard: boolean
  staticCard: boolean

  searchImageSize: ImageSize

  viewableDeckControls: Record<StickiableControls, boolean>

  quickAddOptions: QuickAddOptions
}

export type QuickAddOptions = {
  addType: ADD_TYPES
  dontClearOnSelect: boolean
  smartQuickAdd: boolean
  incrementOnDuplicate: boolean
  matchExistingBy: 'name' | 'uid' | 'never'
  addToCategoryName: string
}

export type TextColumns = {
  manaCost: boolean
  type: boolean
  price: boolean
  setName: boolean
  setSymbol: boolean
  colorTag: boolean
  collectionInfo: boolean
  pinnedInfo: boolean
  dragHandle: boolean
  finishes: boolean
  gamechangers: boolean
  extraOptions: boolean
  canlanderPoints: boolean
}

export type ImageCardLayers = {
  foilLayer: boolean
  qtyAndColorTag: boolean
  qtyControls: boolean
  openDetailsButton: boolean
  cardImage: boolean
  collectionRibbon: boolean
  errorBorder: boolean
  gameChangerIndicator: boolean
  flipButton: boolean
  canlanderPoints: boolean
  customCardIndicator: boolean
  secondaryCategoryIndicator: boolean
  pinnedCardStatus: boolean
}

export type ImageSize = 1 | 2 | 3

export type StickiableControls =
  | 'cardSearch'
  | 'import'
  | 'export'
  | 'clone'
  | 'addToCollection'
  | 'optimize'
  | 'selectAll'
  | 'packages'
  | 'stats'
  | 'history'
  | 'settings'
  | 'lands'
  | 'customCards'
  | 'buySell'
  | 'edhrec'
  | 'share'
  | 'compare'

export const generateInitialState = (serverCookies?: Record<string, any> | string): CachablesState => {
  const cachedState = getCachedState(serverCookies)

  return {
    textColumns: {
      dragHandle: cachedState?.textColumns?.dragHandle ?? true,
      extraOptions: cachedState?.textColumns?.extraOptions ?? true,
      manaCost: cachedState?.textColumns?.manaCost ?? true,
      type: cachedState?.textColumns?.type ?? false,
      price: cachedState?.textColumns?.price ?? false,
      setName: cachedState?.textColumns?.setName ?? false,
      setSymbol: cachedState?.textColumns?.setSymbol ?? false,
      colorTag: cachedState?.textColumns?.colorTag ?? false,
      collectionInfo: cachedState?.textColumns?.collectionInfo ?? false,
      pinnedInfo: cachedState?.textColumns?.pinnedInfo ?? false,
      finishes: cachedState?.textColumns?.finishes ?? false,
      gamechangers: cachedState?.textColumns?.gamechangers ?? true,
      canlanderPoints: cachedState?.textColumns?.canlanderPoints ?? false,
    },

    imageCardLayers: {
      foilLayer: cachedState?.imageCardLayers?.foilLayer ?? true,
      qtyAndColorTag: cachedState?.imageCardLayers?.qtyAndColorTag ?? true,
      qtyControls: cachedState?.imageCardLayers?.qtyControls ?? true,
      openDetailsButton: cachedState?.imageCardLayers?.openDetailsButton ?? true,
      cardImage: cachedState?.imageCardLayers?.cardImage ?? true,
      collectionRibbon: cachedState?.imageCardLayers?.collectionRibbon ?? true,
      errorBorder: cachedState?.imageCardLayers?.errorBorder ?? true,
      gameChangerIndicator: cachedState?.imageCardLayers?.gameChangerIndicator ?? true,
      flipButton: cachedState?.imageCardLayers?.flipButton ?? true,
      canlanderPoints: cachedState?.imageCardLayers?.canlanderPoints ?? true,
      customCardIndicator: cachedState?.imageCardLayers?.customCardIndicator ?? true,
      secondaryCategoryIndicator: cachedState?.imageCardLayers?.secondaryCategoryIndicator ?? true,
      pinnedCardStatus: cachedState?.imageCardLayers?.pinnedCardStatus ?? true,
    },

    imageSize: cachedState?.imageSize ?? 2,
    disableMargins: cachedState?.disableMargins ?? false,
    showEmptyCardStacks: cachedState?.showEmptyCardStacks ?? false,
    hideDefaultTypeCategories: cachedState?.hideDefaultTypeCategories ?? false,
    pinnedCategory: cachedState?.pinnedCategory ?? false,
    draggableStacks: cachedState?.draggableStacks ?? false,
    hideOutOfDeckCategories: cachedState?.hideOutOfDeckCategories ?? false,
    disableDeckCursorCard: cachedState?.disableDeckCursorCard ?? false,
    staticCard: cachedState?.staticCard ?? false,

    searchImageSize: cachedState?.searchImageSize ?? 2,

    viewableDeckControls: {
      cardSearch: cachedState?.viewableDeckControls?.cardSearch ?? false,
      import: cachedState?.viewableDeckControls?.import ?? false,
      export: cachedState?.viewableDeckControls?.export ?? false,
      clone: cachedState?.viewableDeckControls?.clone ?? false,
      addToCollection: cachedState?.viewableDeckControls?.addToCollection ?? false,
      optimize: cachedState?.viewableDeckControls?.optimize ?? false,
      selectAll: cachedState?.viewableDeckControls?.selectAll ?? false,
      packages: cachedState?.viewableDeckControls?.packages ?? false,
      stats: cachedState?.viewableDeckControls?.stats ?? false,
      history: cachedState?.viewableDeckControls?.history ?? false,
      settings: cachedState?.viewableDeckControls?.settings ?? true,
      lands: cachedState?.viewableDeckControls?.lands ?? false,
      customCards: cachedState?.viewableDeckControls?.customCards ?? false,
      buySell: cachedState?.viewableDeckControls?.buySell ?? false,
      edhrec: cachedState?.viewableDeckControls?.edhrec ?? true,
      share: cachedState?.viewableDeckControls?.share ?? false,
      compare: cachedState?.viewableDeckControls?.compare ?? false,
    },

    quickAddOptions: {
      addType: cachedState?.quickAddOptions?.addType || 1,
      dontClearOnSelect: cachedState?.quickAddOptions?.dontClearOnSelect ?? false,
      smartQuickAdd: cachedState?.quickAddOptions?.smartQuickAdd ?? false,
      incrementOnDuplicate: cachedState?.quickAddOptions?.incrementOnDuplicate ?? false,
      matchExistingBy: cachedState?.quickAddOptions?.matchExistingBy || 'uid',
      addToCategoryName: cachedState?.quickAddOptions?.addToCategoryName || '',
    },
  }
}

export default generateInitialState
